import React, { createContext, useContext, useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import api from '../api';
import { getActivePaidFeatures, PaidFeatureExpiries } from '../utils/premiumAccess';
import { speakText } from '../utils/speech';
import { DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY } from '../constants/discussionRooms';

interface User {
  _id: string;
  name: string;
  email: string;
  referralCode?: string;
  referralCount?: number;
  referralDiscountCredits?: Record<string, number>;
  referralDiscountPercent?: number;
  role?: string;
  school?: string;
  schoolName?: string;
  schoolEmail?: string;
  className?: string;
  registrationId?: string;
  department?: 'Science' | 'Commercial' | 'Art' | string;
  jambSubjects?: string[];
  educationLevel?: string;
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;
  purpose?: string;
  address?: string;
  city?: string;
  country?: string;
  isSubscribed?: boolean;
  isPremium?: boolean;
  isGoldMember?: boolean;
  isSchoolSubscribed?: boolean;
  paidFeatures?: Record<string, boolean>;
  paidFeatureExpiries?: PaidFeatureExpiries;
  googleId?: string;
  profilePhoto?: string;
  profileLocked?: boolean;
  reportCardPublished?: boolean;
  examTicketPublished?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, secretCode?: string) => Promise<User>;
  parentLogin: (studentId: string, parentPhone: string) => Promise<User>;
  confirmSessionReplacement: (challengeToken: string, proof?: { password?: string; parentPhone?: string }) => Promise<User>;
  logout: () => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  updateUser: (userData: User) => void;
  setToken: (token: string | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const normalizeUserPremiumState = (userData: any): User | null => {
  if (!userData) return null;

  const paidFeatures = getActivePaidFeatures(userData.paidFeatures, userData.paidFeatureExpiries);
  const hasAnyPaidFeature = Object.values(paidFeatures).some(Boolean);

  const normalized = {
    ...userData,
    paidFeatures,
    isPremium: Boolean(
      userData.isPremium ||
      userData.isSubscribed ||
      userData.isGoldMember ||
      userData.isSchoolSubscribed ||
      hasAnyPaidFeature
    ),
    isSubscribed: Boolean(userData.isSubscribed || userData.isSchoolSubscribed),
  };

  return normalized as User;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionReplacedNoticeOpen, setSessionReplacedNoticeOpen] = useState(false);

  useEffect(() => {
    const handleSessionReplaced = () => {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
      setToken(null);
      setUser(null);
      setSessionReplacedNoticeOpen(true);
    };
    window.addEventListener('auth:session-replaced', handleSessionReplaced);
    return () => window.removeEventListener('auth:session-replaced', handleSessionReplaced);
  }, []);

  useEffect(() => {
    if (!token) return;
    const checkSession = () => {
      if (document.visibilityState === 'visible') {
        api.get('/auth/me').catch(() => {});
      }
    };
    const interval = window.setInterval(checkSession, 5000);
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', checkSession);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', checkSession);
    };
  }, [token]);

  // Load token and user from localStorage on mount
  useEffect(() => {
    // Load token and user from localStorage; if token exists but user missing,
    // treat as authenticated and fetch profile from backend to populate user.
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');

    let mounted = true;

    const bootstrap = async () => {
      if (storedToken) {
        setToken(storedToken);
      }

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);

          const normalizedParsedUser = normalizeUserPremiumState(parsedUser);
          // Always refresh the profile so department and other account changes are
          // available immediately, while retaining the cached user during loading.
          setUser(normalizedParsedUser);
          try {
            const res = await api.get('/users/profile');
            if (res?.data?.data) {
              const profile = normalizeUserPremiumState(res.data.data);
              if (mounted) setUser(profile);
              try { localStorage.setItem('user', JSON.stringify(profile)); } catch (e) {}
              console.debug('[AuthContext] Refreshed user profile from backend during bootstrap');
              if (mounted) setIsLoading(false);
              return;
            }
          } catch (err: any) {
            const status = err?.response?.status ?? undefined;
            const message = err?.message ?? String(err);
            console.warn('[AuthContext] Failed to refresh profile during bootstrap:', status || message);
            // keep parsedUser as fallback
            if (mounted) setIsLoading(false);
            return;
          }
        } catch (err) {
          console.error('[AuthContext] Failed to parse stored user:', err);
          localStorage.removeItem('user');
        }
      }

      // If we have a token but no stored user, try to fetch profile from backend
      if (storedToken && !storedUser) {
        try {
          const res = await api.get('/users/profile');
          if (res?.data?.data) {
            const profile = normalizeUserPremiumState(res.data.data);
            if (mounted) setUser(profile);
            try {
              localStorage.setItem('user', JSON.stringify(profile));
            } catch (e) {}
            console.debug('[AuthContext] Retrieved user profile from backend during bootstrapping');
          }
        } catch (err: any) {
          const status = err?.response?.status ?? undefined;
          const message = err?.message ?? String(err);
          console.error('[AuthContext] Failed to fetch profile during bootstrap:', status || message);
          // Only clear token for authentication errors (invalid/expired). For other errors (429 rate-limit), keep token.
          if (status === 401 || status === 403) {
            try { localStorage.removeItem('auth_token'); } catch (e) {}
            if (mounted) setToken(null);
          }
        }
      }

      if (mounted) setIsLoading(false);
    };

    bootstrap();

    return () => { mounted = false; };
  }, []);

  const handleAuthResponse = async (res: any): Promise<User> => {
    const newToken = res.data?.token;
    let userData = res.data?.data;

    if (newToken && userData) {
      localStorage.removeItem(DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY);
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);

      try {
        const profileRes = await api.get('/users/profile');
        if (profileRes?.data?.data) {
          userData = profileRes.data.data;
        }
      } catch (e) {
        console.warn('[AuthContext] Could not fetch profile after login, using login payload', e);
      }

      const normalizedUser = normalizeUserPremiumState(userData);
      localStorage.removeItem('admin_auth_token');
      localStorage.removeItem('admin_user');
      localStorage.removeItem(DISCUSSION_ROOM_WIDGET_INSTRUCTION_DISMISSED_KEY);
      try { localStorage.setItem('user', JSON.stringify(normalizedUser)); } catch (e) {}
      setUser(normalizedUser);
      speakText(`Welcome, ${normalizedUser?.name || normalizedUser?.parentName || 'User'}.`);
      return normalizedUser as User;
    }

    throw new Error('Invalid login response');
  };

  const login = async (email: string, password: string, secretCode?: string): Promise<User> => {
    const res = await api.post('/auth/login', { email, password, secretCode });
    return handleAuthResponse(res);
  };

  const parentLogin = async (studentId: string, parentPhone: string): Promise<User> => {
    const res = await api.post('/auth/login', { studentId, parentPhone });
    return handleAuthResponse(res);
  };

  const confirmSessionReplacement = async (challengeToken: string, proof: { password?: string; parentPhone?: string } = {}): Promise<User> => {
    const res = await api.post('/auth/confirm-session-replacement', { challengeToken, ...proof });
    return handleAuthResponse(res);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // ignore logout errors
    } finally {
      // CRITICAL: Clear all auth data (user, admin, and any old session state)
      try {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        localStorage.removeItem('admin_auth_token');
        localStorage.removeItem('admin_user');
        // Clear any per-tab admin unlock tokens stored in sessionStorage so next login requires password again
        try {
          const keysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith('admin_tab_pw_')) keysToRemove.push(k);
          }
          keysToRemove.forEach(k => sessionStorage.removeItem(k));
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore any errors during cleanup
      }
      setToken(null);
      setUser(null);
    }
  };

  const register = async (name: string, email: string, password: string, schoolName?: string, schoolEmail?: string, educationLevel?: string, purpose?: string, address?: string, city?: string, country?: string) => {
    await api.post('/auth/register', { 
      name, 
      email, 
      password,
      schoolName,
      schoolEmail,
      educationLevel,
      purpose,
      address,
      city,
      country
    });
  };

  const updateUser = (userData: User) => {
    const normalizedUser = normalizeUserPremiumState(userData) || userData;
    setUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const setTokenValue = (newToken: string | null) => {
    setToken(newToken);
    if (newToken) {
      localStorage.setItem('auth_token', newToken);
    } else {
      localStorage.removeItem('auth_token');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        parentLogin,
        confirmSessionReplacement,
        logout,
        register,
        updateUser,
        setToken: setTokenValue,
      }}
    >
      {children}
      <Dialog open={sessionReplacedNoticeOpen} onClose={() => setSessionReplacedNoticeOpen(false)}>
        <DialogTitle>Signed out on this device</DialogTitle>
        <DialogContent>
          <Typography>This account was signed in on another device, so this session has ended.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionReplacedNoticeOpen(false)} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};